/**
 * API Mutation Hook Factory
 *
 * Provides a consistent way to create mutations with built-in:
 * - Error handling and toast notifications
 * - Success messages
 * - Logging
 * - Type safety
 *
 * Handles the Convex Result pattern where mutations return:
 * { success: true, data: T } | { success: false, error: CustomError }
 */

import { useConvexMutation } from "@convex-dev/react-query";
import { useMutation, type UseMutationResult } from "@tanstack/react-query";
import { useLogger } from "@/packages/frontend-logger";
import type { FunctionReference } from "convex/server";
import { showErrorToast, showSuccessToast } from "../errors";
import { ApiError, type BackendErrorData } from "../errors/types";

/**
 * Configuration for the API mutation hook
 */
export interface ApiMutationConfig<TData, TVariables> {
  /** Mutation name for logging (e.g., "delete-document") */
  name: string;

  /**
   * Success message to show in toast.
   * Can be a string or a function that receives the data.
   * If undefined, no success toast is shown.
   */
  successMessage?: string | ((data: TData, variables: TVariables) => string);

  /**
   * Custom error handler.
   * Return `false` to prevent the default error toast.
   * Return `true` or `void` to show the default error toast.
   */
  onError?: (error: ApiError, variables: TVariables) => boolean | undefined;

  /**
   * Custom success handler.
   * Called after the mutation succeeds (after success toast if any).
   */
  onSuccess?: (data: TData, variables: TVariables) => void;

  /**
   * Disable automatic error toast.
   * Useful when you want to handle errors completely custom.
   * @default false
   */
  silentErrors?: boolean;

  /**
   * Disable automatic success toast.
   * @default false
   */
  silentSuccess?: boolean;

  /**
   * Show "View Details" button on error toast.
   * @default true
   */
  showErrorDetails?: boolean;

  /**
   * Additional context for logging.
   */
  logContext?: Record<string, unknown>;
}

/**
 * Return type of useApiMutation, extending TanStack Query's UseMutationResult
 */
export type ApiMutationResult<TData, TVariables> = UseMutationResult<
  TData,
  ApiError,
  TVariables,
  unknown
>;

/**
 * Backend Result type returned by Convex mutations using makeHandler.
 * Mutations always return HTTP 200 but indicate success/failure in the payload.
 */
type ConvexResult<T> =
  | { success: true; data: T }
  | { success: false; error: BackendErrorData };

/**
 * Extracts the inner data type from a makeHandler Result wrapper.
 * When a mutation uses makeHandler, it returns ConvexResult<T> which is a union:
 *   { success: true; data: T } | { success: false; error: CustomError }
 * We extract T from the success branch only, discarding the failure branch
 * (failures are thrown as ApiError by mutationFn, never returned).
 * Falls back to TResult unchanged for mutations that don't use makeHandler.
 */
type UnwrapResult<TResult> =
  Extract<TResult, { success: true }> extends { success: true; data: infer D }
    ? D
    : TResult;

/**
 * Creates a mutation hook with consistent error handling, logging, and toasts.
 *
 * @example
 * ```tsx
 * // In a feature hook file
 * export function useDeleteDocument() {
 *   const mutation = useApiMutation(api.documents.update, {
 *     name: "delete-document",
 *     successMessage: "Document deleted",
 *     onSuccess: () => closeModal(),
 *   });
 *
 *   return {
 *     deleteDocument: (id: Id<"documents">) =>
 *       mutation.mutateAsync({ id, isDeleted: true }),
 *     isPending: mutation.isPending,
 *     error: mutation.error,
 *   };
 * }
 * ```
 *
 * @example
 * ```tsx
 * // With dynamic success message
 * const mutation = useApiMutation(api.documents.rename, {
 *   name: "rename-document",
 *   successMessage: (data) => `Renamed to "${data.fileName}"`,
 * });
 * ```
 *
 * @example
 * ```tsx
 * // With custom error handling
 * const mutation = useApiMutation(api.documents.delete, {
 *   name: "delete-document",
 *   onError: (error) => {
 *     if (error.code === "PERMISSION_DENIED") {
 *       showCustomPermissionDialog();
 *       return false; // Prevent default toast
 *     }
 *     // Return true/void to show default toast
 *   },
 * });
 * ```
 */
export function useApiMutation<
  TResult,
  TVariables extends Record<string, unknown>,
>(
  mutationRef: FunctionReference<"mutation", "public", TVariables, TResult>,
  config: ApiMutationConfig<UnwrapResult<TResult>, TVariables>,
): ApiMutationResult<UnwrapResult<TResult>, TVariables> {
  const convexMutation = useConvexMutation(mutationRef);
  const logger = useLogger().withContext({
    mutation: config.name,
    ...config.logContext,
  });

  return useMutation({
    mutationFn: async (
      variables: TVariables,
    ): Promise<UnwrapResult<TResult>> => {
      // @ts-expect-error: TypeScript can't resolve OptionalRestArgs conditional type for generic TVariables
      const result = (await convexMutation(variables)) as ConvexResult<
        UnwrapResult<TResult>
      >;

      logger.debug("Mutation result", result);

      // Handle Result pattern: { success: false, error } → throw ApiError
      if (!result.success) {
        throw new ApiError(result.error);
      }

      // Success: return the actual data
      return result.data;
    },

    onSuccess: (data: UnwrapResult<TResult>, variables: TVariables) => {
      logger.debug("Mutation succeeded", { data });

      // Show success toast if configured
      if (!config.silentSuccess && config.successMessage) {
        const message =
          typeof config.successMessage === "function"
            ? config.successMessage(data, variables)
            : config.successMessage;
        showSuccessToast(message);
      }

      // Call custom success handler
      config.onSuccess?.(data, variables);
    },

    onError: (error, variables) => {
      logger.error("Mutation failed", {
        error:
          typeof error.toJSON === "function"
            ? error.toJSON()
            : { message: error.message, name: error.name },
        variables,
      });

      // Call custom error handler
      const handled = config.onError?.(error, variables);

      // Show error toast unless silenced or handled
      if (!config.silentErrors && handled !== false) {
        showErrorToast(error, {
          showDetailsButton: config.showErrorDetails ?? true,
        });
      }
    },
  });
}
